import styled, { css } from 'styled-components'
import colorsOptions from '../../fundamentals/colors-styles/constants'
import {
  textStyleSizeOptions,
  textStyleWeightOptions,
  textStyleSizeStyleOptions,
  textStyleWeightStyleOptions
} from '../../fundamentals/text-styles/constants'
import {
  tooltipSizeStyleOptions,
  tooltipColorStyleOptions,
  tooltipSizeOptions,
  tooltipColorOptions,
  tooltipSideOptions,
  tooltipAlignOptions,
  tooltipArrowAlignmentOptions
} from './constants'

const StyledTooltipRoot = styled.div`
  position: relative;
  display: block;
  width: fit-content;
`

const StyledPortal = styled.div`
  position: absolute;
  z-index: 9999;
`

const StyledTooltipContent = styled.div`
  color: ${colorsOptions.greys.white};
  border-radius: 1px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  position: absolute;
  z-index: 9999;
  ${textStyleSizeStyleOptions[textStyleSizeOptions['TEXT-XS']]}
  ${textStyleWeightStyleOptions[textStyleWeightOptions['TEXT-500']]}

  ${({ size = tooltipSizeOptions.MEDIUM }) => css`
    ${tooltipSizeStyleOptions[size]}
  `};

  ${({ color = tooltipColorOptions.DEFAULT }) => css`
    ${tooltipColorStyleOptions[color]}
  `};

  ${({
    side = tooltipSideOptions.BOTTOM,
    align = tooltipAlignOptions.CENTER,
    sideOffset = 0
  }) => {
    let marginValue = '0'
    if (side === tooltipSideOptions.BOTTOM || side === tooltipSideOptions.TOP) {
      if (align === tooltipAlignOptions.START) {
        marginValue = `margin-right: ${sideOffset}px;`
      } else if (align === tooltipAlignOptions.END) {
        marginValue = `margin-left: ${sideOffset}px;`
      }
    } else if (
      side === tooltipSideOptions.LEFT ||
      side === tooltipSideOptions.RIGHT
    ) {
      if (align === tooltipAlignOptions.START) {
        marginValue = `margin-bottom: ${sideOffset}px;`
      } else if (align === tooltipAlignOptions.END) {
        marginValue = `margin-top: ${sideOffset}px;`
      }
    }

    return css`
      ${marginValue}
    `
  }};

  ${({ side = tooltipSideOptions.BOTTOM }) => {
    switch (side) {
      case 'top':
        return css`
          bottom: calc(100% + 8px);
          left: 50%;
          transform: translateX(-50%);
        `
      case 'right':
        return css`
          top: 50%;
          left: calc(100% + 8px);
          transform: translateY(-50%);
        `
      case 'bottom':
        return css`
          top: calc(100% + 8px);
          left: 50%;
          transform: translateX(-50%);
        `
      case 'left':
        return css`
          top: 50%;
          right: calc(100% + 8px);
          transform: translateY(-50%);
        `
      default:
        return null
    }
  }}

  ${({
    align = tooltipAlignOptions.CENTER,
    side = tooltipSideOptions.BOTTOM
  }) => {
    if (
      (side === tooltipSideOptions.TOP || side === tooltipSideOptions.BOTTOM) &&
      (align === tooltipAlignOptions.START || align === tooltipAlignOptions.END)
    ) {
      return css`
        right: ${align === tooltipAlignOptions.START ? '0' : 'auto'};
        left: ${align === tooltipAlignOptions.END ? '0' : 'auto'};
        transform: none;
      `
    }
    if (
      (side === tooltipSideOptions.LEFT || side === tooltipSideOptions.RIGHT) &&
      (align === tooltipAlignOptions.START || align === tooltipAlignOptions.END)
    ) {
      return css`
        bottom: ${align === tooltipAlignOptions.START ? '0' : 'auto'};
        top: ${align === tooltipAlignOptions.END ? '0' : 'auto'};
        transform: none;
      `
    }
    return null
  }}
`

const StyledTooltipTrigger = styled.div`
  cursor: default;
`

const StyledTooltipArrow = styled.div`
  width: 0;
  height: 0;
  border-style: solid;
  border-color: black;
  position: absolute;
  border-width: 5px;
  border-style: solid;

  ${({ side, arrowAlignment, color = tooltipColorOptions.DEFAULT }) => {
    let leftValue
    let marginLeftValue
    let topValue
    let marginTopValue

    if (arrowAlignment === tooltipArrowAlignmentOptions.START) {
      leftValue = '0%'
      marginLeftValue = '5px'
      topValue = '0%'
      marginTopValue = '5px'
    } else if (arrowAlignment === tooltipArrowAlignmentOptions.CENTER) {
      leftValue = '50%'
      marginLeftValue = '-5px'
      topValue = '50%'
      marginTopValue = '-5px'
    } else if (arrowAlignment === tooltipArrowAlignmentOptions.END) {
      leftValue = '100%'
      marginLeftValue = '-15px'
      topValue = '100%'
      marginTopValue = '-15px'
    } else {
      leftValue = '50%'
      marginLeftValue = '-5px'
      topValue = '50%'
      marginTopValue = '-5px'
    }

    switch (side) {
      case 'top':
        return css`
          top: 100%;
          border-color: ${tooltipColorStyleOptions[color].backgroundColor}
            transparent transparent transparent;
          left: ${leftValue};
          margin-left: ${marginLeftValue};
        `
      case 'right':
        return css`
          right: 100%;
          border-color: transparent
            ${tooltipColorStyleOptions[color].backgroundColor} transparent
            transparent;
          top: ${topValue};
          margin-top: ${marginTopValue};
        `
      case 'bottom':
        return css`
          bottom: 100%;
          border-color: transparent transparent
            ${tooltipColorStyleOptions[color].backgroundColor} transparent;
          left: ${leftValue};
          margin-left: ${marginLeftValue};
        `
      case 'left':
        return css`
          left: 100%;
          border-color: transparent transparent transparent
            ${tooltipColorStyleOptions[color].backgroundColor};
          top: ${topValue};
          margin-top: ${marginTopValue};
        `
      default:
        return null
    }
  }}
`

const StyledTooltipButton = styled.div`
  background-color: ${colorsOptions.docuToolsOrange['dt-10']};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  border-radius: 4px;
`

const StyledContentChildren = styled.div`
  max-width: 160px;
  word-wrap: break-word;
  max-height: 160px;
  overflow: auto;
  width: max-content;
  height: max-content;
`

export {
  StyledPortal,
  StyledTooltipRoot,
  StyledTooltipContent,
  StyledTooltipTrigger,
  StyledTooltipArrow,
  StyledTooltipButton,
  StyledContentChildren
}
